import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Reward, NewReward } from '@/services/rewardService'; // Assuming Reward and NewReward types are exported
import { 
  Gift, Star, Award, Trophy, Coffee, Film, ShoppingCart, CalendarPlus, PartyPopper, Zap, Icon as LucideIcon 
} from 'lucide-react';
import { cn } from '@/lib/utils'; // For conditional class names

// Define available icons for selection
const availableIcons: { name: string; component: LucideIcon }[] = [
  { name: 'Gift', component: Gift },
  { name: 'Star', component: Star },
  { name: 'Award', component: Award },
  { name: 'Trophy', component: Trophy },
  { name: 'Coffee', component: Coffee },
  { name: 'Film', component: Film },
  { name: 'ShoppingCart', component: ShoppingCart },
  { name: 'CalendarPlus', component: CalendarPlus },
  { name: 'PartyPopper', component: PartyPopper },
  { name: 'Zap', component: Zap },
];

interface ManageRewardsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rewardData: NewReward | Reward, isEditing: boolean) => Promise<void>;
  rewardToEdit?: Reward | null;
}

export function ManageRewardsDialog({
  isOpen,
  onClose,
  onSubmit,
  rewardToEdit,
}: ManageRewardsDialogProps) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [pointsRequired, setPointsRequired] = useState('');
  const [selectedIconName, setSelectedIconName] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!rewardToEdit;

  useEffect(() => {
    if (isOpen) {
      if (isEditing && rewardToEdit) {
        setName(rewardToEdit.name);
        setPointsRequired(rewardToEdit.pointsRequired.toString());
        setSelectedIconName(rewardToEdit.iconName || undefined);
      } else {
        // Reset for new reward
        setName('');
        setPointsRequired('');
        setSelectedIconName(undefined); // Default to no icon or the first one
      }
      setIsSubmitting(false);
    }
  }, [isOpen, rewardToEdit, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const pointsNum = parseInt(pointsRequired, 10);

    if (!name.trim()) {
      toast({ title: "Fehler", description: "Prämienname darf nicht leer sein.", variant: "destructive" });
      setIsSubmitting(false);
      return;
    }
    if (isNaN(pointsNum) || pointsNum < 0) {
      toast({ title: "Fehler", description: "Benötigte Punkte müssen eine nicht-negative Zahl sein.", variant: "destructive" });
      setIsSubmitting(false);
      return;
    }
    // Basic URL validation (optional, can be improved)
    // if (imageUrl.trim() && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
    //     toast({ title: "Fehler", description: "Bild-URL muss mit http:// oder https:// beginnen.", variant: "destructive" });
    //     setIsSubmitting(false);
    //     return;
    // }


    const rewardData: NewReward = {
      name: name.trim(),
      pointsRequired: pointsNum,
      iconName: selectedIconName,
    };

    try {
      if (isEditing && rewardToEdit) {
        await onSubmit({ ...rewardData, id: rewardToEdit.id }, true);
      } else {
        await onSubmit(rewardData, false);
      }
      onClose(); // Close dialog on successful submission
    } catch (error) {
      // Error toast is handled by the onSubmit function in the parent typically
      console.error("Error submitting reward form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md"> {/* Adjusted width for icons */}
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Prämie Bearbeiten' : 'Neue Prämie Hinzufügen'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="pointsRequired" className="text-right">
                Punkte
              </Label>
              <Input
                id="pointsRequired"
                type="number"
                value={pointsRequired}
                onChange={(e) => setPointsRequired(e.target.value)}
                className="col-span-3"
                required
                min="0"
              />
            </div>
            {/* Icon Selection */}
            <div className="grid grid-cols-4 items-start gap-4 mt-2">
              <Label className="text-right pt-2">Icon (Optional)</Label>
              <div className="col-span-3 grid grid-cols-5 gap-2">
                {availableIcons.map((icon) => {
                  const IconComponent = icon.component;
                  return (
                    <Button
                      key={icon.name}
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setSelectedIconName(icon.name)}
                      className={cn(
                        "border-2",
                        selectedIconName === icon.name ? "border-primary ring-2 ring-primary ring-offset-2" : "border-gray-300"
                      )}
                      title={icon.name}
                    >
                      <IconComponent className="h-5 w-5" />
                    </Button>
                  );
                })}
                 <Button // Button to clear selection
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setSelectedIconName(undefined)}
                    className={cn(
                        "border-2",
                        !selectedIconName ? "border-primary ring-2 ring-primary ring-offset-2" : "border-gray-300",
                        "text-xs"
                    )}
                    title="Kein Icon"
                    >
                    X
                 </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Abbrechen
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (isEditing ? 'Speichert...' : 'Fügt hinzu...') : (isEditing ? 'Änderungen Speichern' : 'Prämie Hinzufügen')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
} 
